import { FC, useCallback, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'

import { PromptReasoningLevel, PromptType } from '../../types/types'

export type VersionFormValues = {
  version: number
  fullText: string
  months: number
  reasoningLevel: PromptReasoningLevel
  setAsActive: boolean
}

type VersionFormDialogProps = {
  open: boolean
  onClose: () => void
  onSubmit: (values: VersionFormValues) => void
  isLoading: boolean
  promptType: PromptType
  mode: 'create' | 'edit'
  defaultValues?: Partial<VersionFormValues>
}

export const VersionFormDialog: FC<VersionFormDialogProps> = ({ open, onClose, onSubmit, isLoading, promptType, mode, defaultValues }) => {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VersionFormValues>({
    defaultValues: { version: 1, fullText: '', months: 14, reasoningLevel: 'medium', setAsActive: false, ...defaultValues },
  })

  useEffect(() => {
    if (open) reset({ version: 1, fullText: '', months: 14, reasoningLevel: 'medium', setAsActive: false, ...defaultValues })
  }, [open, reset, defaultValues])

  const handleFormSubmit = useCallback((values: VersionFormValues) => onSubmit(values), [onSubmit])

  const title =
    mode === 'create'
      ? t('internal-ai-prompts:version_form_title_create')
      : t('internal-ai-prompts:version_form_title_edit', { version: defaultValues?.version })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {mode === 'create' && (
              <Controller
                name="version"
                control={control}
                rules={{ required: true, min: 1 }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    type="number"
                    label={t('internal-ai-prompts:field_version')}
                    fullWidth
                    size="small"
                    error={!!errors.version}
                  />
                )}
              />
            )}
            <Controller
              name="fullText"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('internal-ai-prompts:field_full_text')}
                  multiline
                  rows={12}
                  fullWidth
                  size="small"
                  error={!!errors.fullText}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
              )}
            />
            {promptType === 'text' && (
              <>
                <Controller
                  name="months"
                  control={control}
                  rules={{ required: true, min: 0 }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      type="number"
                      label={t('internal-ai-prompts:field_months')}
                      fullWidth
                      size="small"
                      error={!!errors.months}
                    />
                  )}
                />
                <Controller
                  name="reasoningLevel"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('internal-ai-prompts:field_reasoning_level')}</InputLabel>
                      <Select {...field} label={t('internal-ai-prompts:field_reasoning_level')}>
                        <MenuItem value="low">Low</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </>
            )}
            <Controller
              name="setAsActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel control={<Checkbox checked={field.value} onChange={field.onChange} />} label={t('internal-ai-prompts:field_set_as_active')} />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('internal-ai-prompts:cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {mode === 'create' ? t('internal-ai-prompts:create') : t('internal-ai-prompts:save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
